import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import {
  getDeliveryChannelUnavailableReason,
  needsEasOrgCode,
} from '../../../../utils/ipawsChannelRules.utils';
import { deliveryChannelOptions, getAvailableWeaHandlingOptions } from '../../config/Ipaws.config';
import IpawsFieldFindings, {
  getIpawsFieldFindingDescriptionId,
  getIpawsFieldFindings,
} from '../IpawsFieldFindings/IpawsFieldFindings';

const getParameterValue = (parameters, name) =>
  (parameters ?? []).find((item) => item.valueName === name)?.value ?? '';
const setParameterValue = (parameters, name, value) => [
  ...(parameters ?? []).filter((item) => item.valueName !== name),
  ...(value ? [{ valueName: name, value }] : []),
];

const formatWeaHandlingOption = (option, { context }) =>
  context === 'menu' ? (
    <span className="d-flex flex-column">
      <span>{option.label}</span>
      {option.description && <small className="text-secondary">{option.description}</small>}
    </span>
  ) : (
    option.label
  );

/**
 * The channel selector (card FE-06). A channel is disabled, with its reason, when it cannot carry
 * the event code (IDG Tables 79/80), when the COG profile has no EAS organization code (EAS and
 * NWEM), or when the COG has no grant for the event code on it (FEMA 415/515/615). The last two
 * apply only to an `Actual` draft, as on the server. `channelRules` comes from
 * `readIpawsChannelRules` (utils/ipawsChannelRules.utils.ts).
 */
const IpawsDeliveryChannelsField = ({
  draft,
  findings,
  environment,
  authorizedWeaHandlingOptions,
  isWeaHandlingLoading = false,
  isWeaHandlingError = false,
  channelRules,
  onDraftChange,
}) => {
  const selected = Array.isArray(draft.deliveryChannels) ? draft.deliveryChannels : [];
  const hasSelectedEventCode = Boolean(draft.eventCode);
  const easOrgCode = channelRules?.easOrgCode ?? null;
  const hasWeaHandlingList = Array.isArray(authorizedWeaHandlingOptions);
  const availableWeaHandlingOptions = getAvailableWeaHandlingOptions(authorizedWeaHandlingOptions, {
    environment,
    eventCode: draft.eventCode,
    status: draft.status,
    urgency: draft.urgency,
    severity: draft.severity,
    certainty: draft.certainty,
  });
  const selectedWeaHandling = getParameterValue(draft.parameters, 'WEAHandling');
  const selectedWeaHandlingOption = findSelectOption(
    availableWeaHandlingOptions,
    selectedWeaHandling,
  );
  const findingId = getIpawsFieldFindingDescriptionId('deliveryChannels', findings);
  const changeParameter = (name, value) =>
    onDraftChange({ parameters: setParameterValue(draft.parameters, name, value) });
  const changeDeliveryChannel = (channel, isChecked) => {
    const deliveryChannels = isChecked
      ? [...selected, channel]
      : selected.filter((value) => value !== channel);
    // Decision D18: EAS-ORG is the COG profile's code. It is never a default value.
    const needsEasOrganizationCode = deliveryChannels.some(needsEasOrgCode);

    onDraftChange({
      deliveryChannels,
      parameters: setParameterValue(
        draft.parameters,
        'EAS-ORG',
        needsEasOrganizationCode ? (easOrgCode ?? '') : '',
      ),
    });
  };

  // The profile answer can arrive after EAS or NWEM is selected, or an Update can restore a value
  // that differs from the profile. A known profile code then replaces the draft value.
  const selectedEasOrgCode = getParameterValue(draft.parameters, 'EAS-ORG');
  const needsSelectedEasOrgCode = selected.some(needsEasOrgCode);
  const reconcilesEasOrgCode =
    needsSelectedEasOrgCode && !!easOrgCode && selectedEasOrgCode !== easOrgCode;

  // Only a loaded COG list, judged against a known environment, may clear a chosen value. While
  // the FEMA meta or jurisdictions are loading (or failed) there is nothing to judge it by, and
  // clearing then would silently drop a restored or updated alert's WEA handling.
  const canReconcileWeaHandling = Array.isArray(authorizedWeaHandlingOptions) && !!environment;
  const clearsWeaHandling =
    canReconcileWeaHandling &&
    !!selectedWeaHandling &&
    !availableWeaHandlingOptions.some(({ value }) => value === selectedWeaHandling);

  // One write for both reconciles. Two writes in one commit both start from the same `draft`, so
  // the second write discards the first.
  useEffect(() => {
    if (!reconcilesEasOrgCode && !clearsWeaHandling) return;
    let parameters = draft.parameters;
    if (reconcilesEasOrgCode) parameters = setParameterValue(parameters, 'EAS-ORG', easOrgCode);
    if (clearsWeaHandling) parameters = setParameterValue(parameters, 'WEAHandling', '');
    onDraftChange({ parameters });
  }, [reconcilesEasOrgCode, clearsWeaHandling, easOrgCode]);

  return (
    <div className="alert-creator-delivery-channels">
      <span
        id="ipaws-alert-delivery-channels-label"
        className="alert-creator-control-label d-block"
      >
        Delivery channels *
      </span>
      <div
        className="alert-creator-delivery-channel-options d-grid gap-2"
        role="group"
        aria-labelledby="ipaws-alert-delivery-channels-label"
        aria-describedby={findingId}
        aria-invalid={
          getIpawsFieldFindings('deliveryChannels', findings).length ? 'true' : undefined
        }
      >
        {deliveryChannelOptions.map((channel) => {
          const unavailableReason = getDeliveryChannelUnavailableReason(
            channel,
            draft.eventCode ?? '',
            channelRules,
            draft.status,
          );
          const available = !unavailableReason;
          const id = `ipaws-alert-delivery-channel-${channel.value.toLowerCase()}`;
          return (
            <div key={channel.value} className="alert-creator-channel-option rounded-3 p-3">
              <Form.Check
                id={id}
                className="alert-creator-delivery-channel d-flex align-items-start gap-2"
                type="checkbox"
                label={channel.label}
                value={channel.value}
                checked={selected.includes(channel.value)}
                disabled={!available}
                onChange={({ target }) => changeDeliveryChannel(target.value, target.checked)}
                aria-describedby={`${id}-reach${available ? '' : ` ${id}-reason`}`}
              />
              <p id={`${id}-reach`} className="small mb-0 mt-2 text-secondary">
                {channel.reach}
              </p>
              {!available ? (
                <p id={`${id}-reason`} className="small alert-creator-finding mb-0 mt-1">
                  {unavailableReason}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
      <IpawsFieldFindings fieldPath="deliveryChannels" findings={findings} />
      <IpawsFieldFindings fieldPath="EAS-ORG" findings={findings} />
      {draft.languages?.some(({ language }) => language === 'es-US') &&
      selected.includes('NWEM') ? (
        <p className="small alert-creator-finding mb-0 mt-2" role="note">
          English and Spanish content are supported for WEA and EAS. Spanish content will remain
          included in the CAP alert, but NOAA Weather Radio currently processes the English version
          for NWEM.
        </p>
      ) : null}
      {selected.includes('CMAS') ? (
        <fieldset className="alert-creator-channel-fields border rounded-3 p-3 mt-3">
          <legend className="alert-creator-control-label px-1">WEA delivery details</legend>
          <Form.Group controlId="ipaws-alert-wea-handling">
            <Form.Label>WEA handling *</Form.Label>
            {/* `required` is what announces the starred state to assistive tech; the value
                itself is enforced by the findings validator, not native form validation. */}
            <BaseSelect
              inputId="ipaws-alert-wea-handling"
              isSearchable
              selectProps={{
                options: availableWeaHandlingOptions,
                value: selectedWeaHandlingOption,
                placeholder: isWeaHandlingLoading
                  ? 'Loading WEA handling options'
                  : !hasSelectedEventCode
                    ? 'Select a SAME event code first'
                    : availableWeaHandlingOptions.length
                      ? 'Select WEA handling'
                      : 'No authorized WEA handling values',
                onChange: (option) => changeParameter('WEAHandling', option?.value ?? ''),
                formatOptionLabel: formatWeaHandlingOption,
                isDisabled:
                  isWeaHandlingLoading ||
                  !hasSelectedEventCode ||
                  availableWeaHandlingOptions.length === 0,
                required: true,
                'aria-required': true,
              }}
            />
            {selectedWeaHandlingOption?.description ? (
              <p className="small mb-0 mt-1 text-secondary" role="note">
                {selectedWeaHandlingOption.description}
              </p>
            ) : null}
            {isWeaHandlingLoading ? (
              <p className="small mb-0 mt-1 text-secondary" role="status">
                Loading authorized WEA handling options.
              </p>
            ) : isWeaHandlingError ? (
              <p className="small alert-creator-finding mb-0 mt-1" role="status">
                Authorized WEA handling values could not be loaded. WEA cannot be sent until the
                FEMA service answers.
              </p>
            ) : !hasSelectedEventCode ? (
              <p className="small mb-0 mt-1 text-secondary" role="status">
                Select a SAME event code to view authorized WEA handling options.
              </p>
            ) : !hasWeaHandlingList ? (
              <p className="small alert-creator-finding mb-0 mt-1" role="status">
                The server did not provide authorized WEA handling values for this COG. WEA is
                unavailable.
              </p>
            ) : authorizedWeaHandlingOptions.length === 0 ? (
              <p className="small alert-creator-finding mb-0 mt-1" role="status">
                This COG has no authorized WEA Handling values.
              </p>
            ) : !availableWeaHandlingOptions.length ? (
              <p className="small alert-creator-finding mb-0 mt-1" role="status">
                No authorized WEA Handling values match this event, operational context, and active
                FEMA environment.
              </p>
            ) : null}
            <IpawsFieldFindings fieldPath="WEAHandling" findings={findings} />
          </Form.Group>
        </fieldset>
      ) : null}
    </div>
  );
};
export default IpawsDeliveryChannelsField;
